import {
  Assignment,
  CommuteOutlined,
  Redeem,
  ScaleOutlined,
  WorkOutlineTwoTone,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HandymanIcon from "@mui/icons-material/Handyman";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";

import { CompanyBusinessTypeChip } from "components/CompanyBusinessTypeChip";
import { ShipmentDetail } from "components/Shipment/components/ShipmentDetail";
import { ShipmentIDText } from "components/Texts/ShipmentIDText";

import { ShipmentRouteSection } from "./ShipmentRouteSection";
import {
  capitalizeFirst,
  formatText,
  formatWeightLb,
  getCommdityNameForCondensedShipmentShipment,
  getEquipmentTypesDisplay,
  getServiceTypeDisplayLabel,
  ISOStringToLocalReadableTimestamp,
} from "../../../../utils";
import { formatQuotingUnitOfMeasure } from "../../../../utils/quotes";

import type { SubmitQuotePreviewShipment } from "./types";

interface ShipmentDetailCardProps {
  shipment: SubmitQuotePreviewShipment;
}

const EMPTY_VALUE = "-";

/**
 * Empty is as absent as null for these: the shipment columns behind them default
 * to "" rather than NULL, and a blank line under a heading reads as a rendering
 * bug rather than as "the shipper did not say".
 */
function displayOrDash(value: string | null): string {
  return value != null && value.length > 0 ? value : EMPTY_VALUE;
}

/**
 * The shipment the emailed link pointed at.
 *
 * The carrier is asked to commit a price off this card and nothing else, so it
 * carries the same attributes the in-app shipment detail shows them — the same
 * fields, the same labels, the same formatters
 * (components/Shipment/components/ShipmentDetails/ShipmentDetails.tsx). Anything
 * it leaves out is a number the carrier has to guess at
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md D7).
 */
export function ShipmentDetailCard(props: ShipmentDetailCardProps) {
  const { shipment } = props;
  const { info, shipment_company: shipmentCompany, project } = shipment;

  return (
    <Paper variant="outlined" sx={{ p: "1rem" }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6">
            {shipmentCompany?.name ?? "Shipment"}
          </Typography>
          {/*
            Shown even when the name above is withheld: whether the counterparty
            is a broker or a direct shipper moves a price, and it identifies no
            one.
          */}
          <CompanyBusinessTypeChip
            businessType={shipmentCompany?.business_type}
          />
        </Stack>
        {/*
          The only identifier on the page. It matters most when the heading above
          falls back to "Shipment" because the shipper's name is withheld from
          this carrier — without it there is nothing to match against the email,
          or to quote to support.
        */}
        <ShipmentIDText shipment={shipment} />

        <ShipmentRouteSection shipment={shipment} />

        <Divider />
        <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
          <ShipmentDetail
            icon={<Redeem fontSize="small" />}
            title="Commodity"
            text={displayOrDash(
              capitalizeFirst(
                formatText(getCommdityNameForCondensedShipmentShipment(info)),
              ),
            )}
          />
          <ShipmentDetail
            icon={<ScaleOutlined fontSize="small" />}
            title="Weight"
            text={
              info.weight_lb != null
                ? formatWeightLb(info.weight_lb)
                : EMPTY_VALUE
            }
          />
          <ShipmentDetail
            icon={<WorkOutlineTwoTone fontSize="small" />}
            title="Service"
            text={getServiceTypeDisplayLabel(info.service)}
          />
          <ShipmentDetail
            icon={<CommuteOutlined fontSize="small" />}
            title="Equipment Type(s)"
            text={
              shipment.equipments.length > 0
                ? getEquipmentTypesDisplay(
                    shipment.equipments.map((equipment) => equipment.name),
                  )
                : EMPTY_VALUE
            }
          />
          <ShipmentDetail
            icon={<SquareFootIcon fontSize="small" />}
            title="Footage"
            text={
              info.footage != null
                ? `${parseFloat(info.footage.toString()).toFixed(1)} ft`
                : EMPTY_VALUE
            }
          />
          <ShipmentDetail
            icon={<Assignment fontSize="small" />}
            title="Product Dimensions"
            multiline
            text={displayOrDash(info.additional_details)}
          />
          <ShipmentDetail
            icon={<HandymanIcon fontSize="small" />}
            title="Special Requirements"
            multiline
            text={displayOrDash(info.special_requirements)}
          />
          {/*
            The unit the quote itself is priced in, so the carrier knows whether
            the number they enter covers the whole load or one unit of it.
          */}
          <ShipmentDetail
            icon={<LocalOfferIcon fontSize="small" />}
            title="Pricing Mode"
            text={formatQuotingUnitOfMeasure(info.quoting_unit_of_measure)}
          />
          {project != null && (
            <ShipmentDetail
              icon={<LocalShippingIcon fontSize="small" />}
              title="Available Shipments"
              text={project.loads_available}
            />
          )}
          <ShipmentDetail
            icon={<AccessTimeIcon fontSize="small" />}
            title="Posted at"
            text={ISOStringToLocalReadableTimestamp(info.created_at)}
          />
        </Box>
      </Stack>
    </Paper>
  );
}
